/* useResetOnFilterChange.ts */

/* React */
import { useEffect, useRef } from "react";

/* React Redux */
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "src/redux/store";
import { resetInsightsFilter } from "src/redux/features/insights-filter/insightsFilterSlice";
import { resetSankeyDiagramOptions } from "src/redux/features/insights-options/treatment-patterns/sankey-diagram/sankeyDiagramOptionsSlice";
import { setDefaultState } from "src/redux/features/custom-cohorts/customCohortsSlice";

/* Interfaces */
import { FilterType } from "src/shared/interfaces/insights-filter/InsightsFilter";

export const useResetOnFilterChange = () => {
  const dispatch = useDispatch<AppDispatch>();
  const insightsFilterState = useSelector(
    (state: RootState) => state.insightsFilter
  );

  const { mainCondition } = insightsFilterState;

  const prevFiltersRef = useRef<
    Partial<Record<FilterType, any>>
  >({});

  const prevMainConditionRef = useRef<
    string | null | undefined
  >();

  useEffect(() => {
    const currentValue = mainCondition?.value;
    const prevValue = prevMainConditionRef.current;

    /**
     * If the value of 'mainCondition' has changed,
     * reset the insights filter, sankey diagram options and
     * custom cohort to their default states.
     */
    if (currentValue && currentValue !== prevValue) {
      dispatch(resetInsightsFilter());
      dispatch(resetSankeyDiagramOptions());
      dispatch(setDefaultState());
    }

    prevMainConditionRef.current = currentValue;
  }, [dispatch, mainCondition?.value]);

  useEffect(() => {
    let hasChanged = false;

    for (const key of Object.keys(
      insightsFilterState
    ) as FilterType[]) {
      const prev = prevFiltersRef.current[key];
      const curr = insightsFilterState[key]?.value;

      if (prev !== curr) {
        hasChanged = true;
        break;
      }
    }

    if (hasChanged) {
      dispatch(resetSankeyDiagramOptions());
    }

    const next = {} as Partial<Record<FilterType, any>>;
    for (const key of Object.keys(
      insightsFilterState
    ) as FilterType[]) {
      next[key] = insightsFilterState[key]?.value;
    }
    prevFiltersRef.current = next;
  }, [insightsFilterState, dispatch]);
};
